class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!customElements.get('shipping-protection')) {
  customElements.define(
    'shipping-protection',
    class ShippingProtection extends HTMLElement {
      connectedCallback() {
        this.input = this.querySelector('input[type="checkbox"]');
        this.status = this.querySelector('[role="status"]');
        if (!this.input || this.input.dataset.bound === 'true') return;

        this.input.dataset.bound = 'true';
        this.input.addEventListener('change', () => this.updateProtection());
        if (this.dataset.autoRemove === 'true') {
          this.input.checked = false;
          this.updateProtection();
        }
      }

      async updateProtection() {
        const shouldAdd = this.input.checked;
        const variantId = Number(this.dataset.variantId);
        const lineKey = this.dataset.lineKey;

        if (!variantId || (!shouldAdd && !lineKey)) {
          this.input.checked = !shouldAdd;
          return;
        }

        this.classList.add('is-loading');
        this.input.disabled = true;
        this.status.textContent = shouldAdd ? this.dataset.addingText : this.dataset.removingText;

        const sections = ['cart-drawer', 'cart-icon-bubble'];
        const body = shouldAdd
          ? {
              items: [{ id: variantId, quantity: 1, properties: { _cart_addon: 'shipping_protection' } }],
              sections,
              sections_url: window.location.pathname,
            }
          : {
              id: lineKey,
              quantity: 0,
              sections,
              sections_url: window.location.pathname,
            };

        try {
          const response = await fetch(shouldAdd ? routes.cart_add_url : routes.cart_change_url, {
            ...fetchConfig(),
            body: JSON.stringify(body),
          });
          const state = await response.json();

          if (!response.ok || state.status || state.errors) {
            throw new Error(state.description || state.message || window.cartStrings.error);
          }

          document.querySelector('cart-drawer')?.renderContents(state);
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: state,
            variantId,
          });
        } catch (error) {
          this.input.checked = !shouldAdd;
          this.input.disabled = false;
          this.classList.remove('is-loading');
          this.status.textContent = error.message || window.cartStrings.error;
        }
      }
    }
  );
}
